// utils/sortConfig.js - Reusable sort configurations

/**
 * Common sort option configurations for different components
 * Usage: import { FARMS_SORT_OPTIONS } from './utils/sortConfig';
 */

// Farms / Root Pools Sort Options
export const FARMS_SORT_OPTIONS = [
    { label: 'Default', value: '' },
    { label: 'Name Ascending', value: 'projectName@ASC' },
    { label: 'Name Descending', value: 'projectName@DESC' },
    { label: 'Reward Amount High to Low', value: 'rewardAmount@DESC' },
    { label: 'Reward Amount Low to High', value: 'rewardAmount@ASC' },
    { label: 'Reward Token A-Z', value: 'rewardToken@ASC' },
    { label: 'Reward Token Z-A', value: 'rewardToken@DESC' }
];

// Staking Pools Sort Options
export const STAKING_SORT_OPTIONS = [
    { label: 'Default', value: '' },
    { label: 'Staking Amount High to Low', value: 'stakingAmount@DESC' },
    { label: 'Staking Amount Low to High', value: 'stakingAmount@ASC' },
    // { label: 'APY High to Low', value: 'apyPercent@DESC' },
    // { label: 'APY Low to High', value: 'apyPercent@ASC' },
    { label: 'Creator Fee High to Low', value: 'creatorFeePercent@DESC' },
    { label: 'Creator Fee Low to High', value: 'creatorFeePercent@ASC' },
    // { label: 'Pool Name A-Z', value: 'poolName@ASC' },
    // { label: 'Pool Name Z-A', value: 'poolName@DESC' }
];
export const STAKES_SORT_OPTIONS = [
    { label: 'Amount High to Low', value: 'amount@desc' },
    { label: 'Amount Low to High', value: 'amount@asc' },
    { label: 'Claim Time Recent First', value: 'claimTime@desc' },
    { label: 'Claim Time Oldest First', value: 'claimTime@asc' },
    { label: 'Block Timestamp Recent', value: 'blockTimestamp@desc' },
    { label: 'Block Timestamp Oldest', value: 'blockTimestamp@asc' },
    // { label: 'Block Number High to Low', value: 'blockNumber@desc' },
    // { label: 'Block Number Low to High', value: 'blockNumber@asc' }
];
// Liquidity Pools Sort Options
export const LIQUIDITY_SORT_OPTIONS = [
    { label: 'TVL High to Low', value: 'tvl@DESC' },
    { label: 'TVL Low to High', value: 'tvl@ASC' },
    { label: 'APR High to Low', value: 'apr@DESC' },
    { label: 'APR Low to High', value: 'apr@ASC' },
    { label: 'Volume High to Low', value: 'volume24h@DESC' },
    { label: 'Volume Low to High', value: 'volume24h@ASC' },
    { label: 'Pair Name A-Z', value: 'pairName@ASC' },
    { label: 'Pair Name Z-A', value: 'pairName@DESC' }
];

// Yield Farms Sort Options
export const YIELD_FARMS_SORT_OPTIONS = [
    { label: 'Multiplier High to Low', value: 'multiplier@DESC' },
    { label: 'Multiplier Low to High', value: 'multiplier@ASC' },
    { label: 'Liquidity High to Low', value: 'liquidity@DESC' },
    { label: 'Liquidity Low to High', value: 'liquidity@ASC' },
    { label: 'Farm Name A-Z', value: 'farmName@ASC' },
    { label: 'Farm Name Z-A', value: 'farmName@DESC' }
];

// NFT Collections Sort Options
export const NFT_SORT_OPTIONS = [
    { label: 'Floor Price High to Low', value: 'floorPrice@DESC' },
    { label: 'Floor Price Low to High', value: 'floorPrice@ASC' },
    { label: 'Volume High to Low', value: 'volume@DESC' },
    { label: 'Volume Low to High', value: 'volume@ASC' },
    { label: 'Collection Name A-Z', value: 'collectionName@ASC' },
    { label: 'Collection Name Z-A', value: 'collectionName@DESC' }
];

// Token List Sort Options
export const TOKEN_SORT_OPTIONS = [
    { label: 'Price High to Low', value: 'price@DESC' },
    { label: 'Price Low to High', value: 'price@ASC' },
    { label: 'Market Cap High to Low', value: 'marketCap@DESC' },
    { label: 'Market Cap Low to High', value: 'marketCap@ASC' },
    { label: 'Volume High to Low', value: 'volume24h@DESC' },
    { label: 'Volume Low to High', value: 'volume24h@ASC' },
    { label: 'Token Name A-Z', value: 'tokenName@ASC' },
    { label: 'Token Name Z-A', value: 'tokenName@DESC' }
];

/**
 * Helper function to create custom sort options
 * @param {string} field - The field name to sort by
 * @param {string} label - Display label for the option
 * @returns {Array} Array of sort options with ASC and DESC
 */
export const createSortOptions = (field, labelPrefix) => {
    return [
        { label: `${labelPrefix} High to Low`, value: `${field}@DESC` },
        { label: `${labelPrefix} Low to High`, value: `${field}@ASC` }
    ];
};

/**
 * Helper function to create name-based sort options
 * @param {string} field - The field name to sort by
 * @param {string} itemType - Type of item (e.g., 'Pool', 'Farm', 'Token')
 * @returns {Array} Array of A-Z and Z-A sort options
 */
export const createNameSortOptions = (field, itemType = 'Name') => {
    return [
        { label: `${itemType} A-Z`, value: `${field}@ASC` },
        { label: `${itemType} Z-A`, value: `${field}@DESC` }
    ];
};

/**
 * Combine multiple sort option arrays
 * @param  {...Array} optionArrays - Multiple arrays of sort options
 * @returns {Array} Combined array of all sort options
 */
export const combineSortOptions = (...optionArrays) => {
    return optionArrays.flat();
};

// Example: Create custom sort options on the fly
// const customSortOptions = combineSortOptions(
//     createSortOptions('stakingAmount', 'Staking Amount'),
//     createSortOptions('apyPercent', 'APY'),
//     createNameSortOptions('poolName', 'Pool Name')
// );